import { useRef, useState } from 'react';
import { CoqueDeLaFiche, GestesDuPied, NotesDeLaFiche, TeteDeLaFiche } from './CoqueDeLaFiche';
import { ContactDeLaFiche } from './ContactDeLaFiche';
import type { ProprietesDeLaFiche } from './Fiche';
import { ceQuElleAttend, lignesDeLaFiche, type PastilleDeFiche } from './ficheDuTelephone';
import { FeuilleDesDelais } from './FeuilleDesDelais';
import { FeuilleDuBas } from '../partage/FeuilleDuBas';
import { NavDeLaFiche } from './NavDeLaFiche';
import { NoteDeLaFiche } from './NoteDeLaFiche';
import { PlanDeLaFiche } from './PlanDeLaFiche';
import { ReglagesAuPouce, type ReglageAuPouce } from './ReglagesAuPouce';
import { useEchap } from '../partage/useEchap';
import { useCouche } from '../partage/surface';
import { IconeFichierDuCarnet } from '../partage/icones';
import { rendreLeFocus, useMecaniqueDeLaFiche } from './useMecaniqueDeLaFiche';
import { laMere } from './ficheEnMots';
import {
  ELLE_ATTEND,
  FERMER_LA_FICHE,
  J_ATTENDS_UN_RETOUR,
  MOTS_DES_TYPES_DE_FICHE,
  REGLAGES,
} from '../partage/vocabulaire';
import './FicheAuPouce.css';

/** Ce que la petite feuille du bas montre en entier : une pastille, ou ce qu'elle attend. */
type Montree = PastilleDeFiche | 'attend' | null;

/**
 * La fiche au téléphone (chantier `telephone-projets-fiche`, ticket 03 ; chantier telephone-fiche,
 * modification 01, maquette `fiche-au-telephone.html`, validée par Igor le 2026-10-26). Une autre
 * vue de la même fiche que l'ordinateur : les mêmes données, les mêmes gestes, rangés pour le
 * pouce, dans la même coque (`CoqueDeLaFiche`) et avec la même mécanique
 * (`useMecaniqueDeLaFiche`). Elle prend le type et le papier de la bulle (`ficheAuTelephone` :
 * sans type choisi, une Note), et les faces de l'ordinateur, réutilisées :
 *
 * - en haut, collé (`.f-tete`) : la poignée, le nom du type dans une étiquette, ⚙ et × ; le titre
 *   à la main derrière l'icône fichier (sauf la Contact, dont la carte porte le nom) ; dessous, le
 *   chemin, « ‹ la mère » et les sœurs (`NavDeLaFiche`) ;
 * - au milieu, qui défile : la Note (`NoteDeLaFiche`), rien d'autre ; la Classique, ce qu'elle
 *   attend puis les champs en lignes pointillées (`ficheDuTelephone.ts`), puis la note ; la
 *   Contact (`ContactDeLaFiche`, ses trois gestes du pouce) ; la Plan (`PlanDeLaFiche`, de haut
 *   en bas) ;
 * - en bas, collé, sous le pouce : les gestes de son état (`GestesDuPied`).
 *
 * Le ⚙ ouvre les Réglages (`ReglagesAuPouce`) : le type et le papier en tête, puis tout le reste
 * de ce que fait la fiche de l'ordinateur. Un champ touché montre l'info en entier dans une
 * petite feuille du bas.
 *
 * Couché (chantier telephone-fiche, 02, maquette `.couche .fiche.f-c2`, `.deux`) : la même fiche,
 * pleine à côté du rail, sans voile ni coins arrondis ; le milieu en deux colonnes, l'essentiel à
 * gauche, le reste à droite (`FicheAuPouce.css`), la Note sur toute la largeur.
 *
 * Dans un projet (chantier telephone-projet, 04), la même fiche se lit en page (`enPage`) : posée
 * sous le fil, sans croix, sans « ‹ la mère » ni sœurs. Couché, posée dans la Journée ou l'Agenda
 * (`FichePosee`), la même en page, en deux colonnes, qui tient dans la place qu'on lui laisse.
 */
export function FicheAuPouce({
  bulle,
  chemin,
  couleur,
  onChanger,
  onValider,
  onAttendre,
  onDefaire,
  annulation,
  onCreuser,
  onFermer,
  onSupprimee,
  onDeplacee,
  onSoeur,
  onMere,
  onEtape,
  onAutreBulle,
  typeEtPapier,
  enPage = false,
}: ProprietesDeLaFiche) {
  const [reglages, setReglages] = useState(false);
  const [reglage, setReglage] = useState<ReglageAuPouce>(null);
  const [montree, setMontree] = useState<Montree>(null);
  const [attendre, setAttendre] = useState(false);
  const boutonReglages = useRef<HTMLButtonElement>(null);
  /*
   * Couché (02) : la même fiche, pleine à côté du rail, en deux colonnes (`.fiche--couchee`) ; en
   * page, posée dans un écran couché, en deux colonnes aussi (la Journée et l'Agenda couchés,
   * chantier telephone-couche-suite, 03, `FichePosee`).
   */
  const couchee = useCouche();

  /* Une autre bulle (une sœur, une étape, celle qu'elle attend) s'ouvre repliée. */
  const mecanique = useMecaniqueDeLaFiche(bulle.id, () => {
    setReglages(false);
    setReglage(null);
    setMontree(null);
    setAttendre(false);
  });
  /*
   * Échap (un clavier branché) replie d'abord ce qui est par-dessus : la petite feuille, puis
   * ce qui est déplié dans le panneau, puis le panneau ; sinon il ferme la fiche. Ses feuilles
   * du bas n'ont pas d'Échap à elles : la fiche le tient pour elles, dans cet ordre. Le titre en
   * champ le garde pour lui ; la suppression, le déplacement et la feuille des délais le
   * prennent avant la fiche (`ordreDEchap`).
   */
  useEchap(true, () => {
    if (montree) setMontree(null);
    else if (reglages && reglage) setReglage(null);
    else if (reglages) fermerLesReglages();
    else onFermer();
  });

  function fermerLesReglages() {
    setReglages(false);
    setReglage(null);
    rendreLeFocus(boutonReglages);
  }
  /* Le nom, un crayon ou « + Contact » de la fiche Contact : les Réglages, sur le contact. */
  function ouvrirLeContact() {
    setReglage('contact');
    setReglages(true);
  }

  const type = typeEtPapier?.montree.type ?? 'classique';
  const attente = ceQuElleAttend(bulle);
  const lignes = lignesDeLaFiche(bulle);

  /* « Attend » : une seule bulle, sa fiche s'ouvre ; plusieurs, la liste se déplie. */
  function toucherLAttente() {
    const seule = attente?.bulles.length === 1 ? attente.bulles[0] : null;
    if (seule && onAutreBulle) onAutreBulle(seule.id);
    else setMontree('attend');
  }

  const tete = (
    <div className="fiche-pouce-tete">
      <div className="fiche-pouce-ligne-h">
        <span className="fiche-pouce-type">{MOTS_DES_TYPES_DE_FICHE[type]}</span>
        <span className="fiche-pouce-boutons">
          <button
            ref={boutonReglages}
            type="button"
            className="fiche-pouce-bouton fiche-pouce-reglages"
            data-tuto="fiche-reglages"
            aria-label={REGLAGES}
            title={REGLAGES}
            aria-haspopup="dialog"
            aria-expanded={reglages}
            onClick={() => setReglages(true)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
              <circle cx="12" cy="12" r="3" />
              <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2" />
            </svg>
          </button>
          {!enPage && (
            <button
              type="button"
              className="fiche-pouce-bouton fiche-pouce-fermer"
              data-tuto="fiche-fermer"
              aria-label={FERMER_LA_FICHE}
              onClick={onFermer}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          )}
        </span>
      </div>
      {type !== 'contact' && (
        <div className="fiche-page-titre">
          <IconeFichierDuCarnet className="fiche-page-io" />
          <TeteDeLaFiche bulle={bulle} mecanique={mecanique} onChanger={onChanger} />
        </div>
      )}
      {!enPage && (
        <NavDeLaFiche bulle={bulle} chemin={chemin} onMere={onMere} onSoeur={onSoeur} onFermer={onFermer} avecCroix={false} />
      )}
    </div>
  );

  /* La Classique : ce qu'elle attend, les champs en lignes pointillées, puis la note. */
  const classique = (
    <div className="fiche-pouce-deux">
      <div className="fiche-pouce-gauche">
        {attente && (
          <div className="fiche-pouce-rangee" data-ligne="attend">
            <button
              type="button"
              className="fiche-pouce-pastille"
              data-sorte="attend"
              aria-haspopup={attente.bulles.length > 1 || !onAutreBulle ? 'dialog' : undefined}
              onClick={toucherLAttente}
            >
              <span className="fiche-pouce-champ-nom">{ELLE_ATTEND}</span>
              <span className="fiche-pouce-puce">
                <span aria-hidden="true">⏸ </span>
                {attente.texte}
              </span>
            </button>
          </div>
        )}
        {/* Les champs en lignes pointillées (maquette `.champ`) : le nom à gauche, la valeur à droite ; toucher la montre en entier. */}
        {lignes.map((ligne) => (
          <div key={ligne.nom} className="fiche-pouce-rangee" data-ligne={ligne.nom}>
            {ligne.pastilles.map((p) => (
              <button
                key={p.sorte}
                type="button"
                className="fiche-pouce-pastille"
                data-sorte={p.sorte}
                aria-haspopup="dialog"
                aria-label={`${p.titre} : ${p.court}`}
                onClick={() => setMontree(p)}
              >
                <span className="fiche-pouce-champ-nom">{p.titre}</span>
                <span className="fiche-pouce-puce">
                  <span aria-hidden="true">{p.signe} </span>
                  {p.court}
                </span>
              </button>
            ))}
          </div>
        ))}
      </div>
      <div className="fiche-pouce-droite fiche-pouce-notes">
        <NotesDeLaFiche bulle={bulle} onChanger={onChanger} />
      </div>
    </div>
  );

  return (
    <CoqueDeLaFiche
      bulle={bulle}
      chemin={chemin}
      couleur={couleur}
      pouce
      couchee={couchee}
      enPage={enPage}
      mecanique={mecanique}
      onMere={onMere}
      onSoeur={onSoeur}
      onFermer={onFermer}
      onSupprimee={onSupprimee}
      onDeplacee={onDeplacee}
      papier={typeEtPapier?.montree.papier ?? 'blanche'}
      type={type}
      sansNav
      haut={tete}
    >
      {/* Ce qui défile, entre l'en-tête et les gestes, qui restent collés. */}
      <div className="fiche-pouce-defile">
        {type === 'note' ? (
          <NoteDeLaFiche bulle={bulle} onChanger={onChanger} />
        ) : type === 'contact' ? (
          <ContactDeLaFiche bulle={bulle} mecanique={mecanique} onPoser={ouvrirLeContact} onChanger={onChanger} auPouce />
        ) : type === 'plan' ? (
          <PlanDeLaFiche key={`plan-${bulle.id}`} bulle={bulle} mere={laMere(chemin)} />
        ) : (
          classique
        )}
      </div>

      <div className="fiche-pied fiche-pied--pouce">
        <GestesDuPied
          bulle={bulle}
          annulation={annulation}
          onValider={onValider}
          onAttendre={() => setAttendre(true)}
          onDefaire={onDefaire}
        />
      </div>

      {/* Le panneau Réglages : le type et le papier, trois groupes, « Ses étapes », Déplacer et Supprimer à part. */}
      {reglages && (
        <FeuilleDuBas
          titre={REGLAGES}
          onFermer={fermerLesReglages}
          {...FEUILLE_DE_LA_FICHE}
          classe={`${FEUILLE_DE_LA_FICHE.classe} fiche-pouce-feuille--large`}
        >
          <ReglagesAuPouce
            bulle={bulle}
            fiche={typeEtPapier}
            ouvert={reglage}
            onOuvrir={setReglage}
            onChanger={onChanger}
            onEtape={onEtape}
            onCreuser={onCreuser}
            onDeplacer={mecanique.deplacer}
            onSupprimer={mecanique.supprimer}
            boutonDeplacer={mecanique.boutonDeplacer}
            boutonSupprimer={mecanique.boutonSupprimer}
          />
        </FeuilleDuBas>
      )}

      {montree === 'attend' && attente && (
        <FeuilleDuBas titre={ELLE_ATTEND} onFermer={() => setMontree(null)} {...FEUILLE_DE_LA_FICHE}>
          <ul className="fiche-pouce-attendues">
            {attente.bulles.map((b) => (
              <li key={b.id}>
                {onAutreBulle ? (
                  <button
                    type="button"
                    className="fiche-pouce-attendue"
                    onClick={() => {
                      setMontree(null);
                      onAutreBulle(b.id);
                    }}
                  >
                    <span>{b.titre}</span>
                    <span aria-hidden="true">›</span>
                  </button>
                ) : (
                  <span className="fiche-pouce-attendue">{b.titre}</span>
                )}
              </li>
            ))}
          </ul>
        </FeuilleDuBas>
      )}

      {montree && montree !== 'attend' && (
        <FeuilleDuBas titre={montree.titre} onFermer={() => setMontree(null)} {...FEUILLE_DE_LA_FICHE}>
          <div className="fiche-pouce-entier" data-sorte={montree.sorte}>
            {montree.entier.map((ligne, i) => (
              <p key={i}>{ligne}</p>
            ))}
          </div>
        </FeuilleDuBas>
      )}

      {attendre && (
        <FeuilleDesDelais
          bulle={bulle}
          titre={J_ATTENDS_UN_RETOUR}
          onChanger={(champs) => onAttendre(champs)}
          onFermer={() => setAttendre(false)}
        />
      )}
    </CoqueDeLaFiche>
  );
}

/**
 * Les feuilles du bas de la fiche (`FeuilleDuBas`) : « OK » ou le voile les referment, et pas
 * d'écoute d'Échap à elles, la fiche le tient pour tout ce qu'elle ouvre.
 */
const FEUILLE_DE_LA_FICHE = { echap: false, ok: true, classe: 'fiche-pouce-feuille' } as const;
